import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { formatAmountForDisplayWithPreferences } from 'utils/index';

const DispenseReport = ({ data, onPageChange, onInvoiceClick }) => {
  const { preferences } = usePreferences();
  // this keeps track of selected rows for highlighting. We want to allow multiple selection of rows so we can compare invoices. We will store the selected invoices in an array.
  const [selectedInvoices, setSelectedInvoices] = React.useState([]);
  const [openDispensingInvoice, setOpenDispensingInvoice] = React.useState(null);

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Dispense Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  const summary = data.summary;

  const handleInvoiceClick = (invoice) => {
    if (onInvoiceClick) {
      onInvoiceClick(invoice);
    }
  }
  
  const handleRowClick = (invoice) => {
    setSelectedInvoices((prevSelected) => {
      const isSelected = prevSelected.some((inv) => inv.id === invoice.id);
      if (isSelected) {
        return prevSelected.filter((inv) => inv.id !== invoice.id);
      } else {
        return [...prevSelected, invoice];
      }
    });
  }

  const openDispensingDialog = (invoice, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setOpenDispensingInvoice(invoice);
  };

  const closeDispensingDialog = () => {
    setOpenDispensingInvoice(null);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Dispense Report ({(data.invoiceType || 'dispense').toUpperCase()})
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Date range: {formatDateWithPreferences(data.dateFrom, preferences)} to {formatDateWithPreferences(data.dateTo, preferences)}
      </Typography>
      {data.location && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}
      {data.generatedAt && (
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Generated: {formatDateWithPreferences(data.generatedAt, preferences)}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Invoices</Typography>
              <Typography variant="h5">{summary.invoiceCount || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Sub Total</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(summary.subTotal, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Discount</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(summary.totalDiscountAmount, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Paid</Typography>
              <Typography variant="h5" sx={{ color: summary.totalCollected > 0 ? 'success.main' : 'error.main' }}>{formatAmountForDisplayWithPreferences(summary.totalCollected, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Refunded</Typography>
              <Typography variant="h5" sx={{ color: summary.totalRefundedAmount > 0 ? 'success.main' : 'error.main' }}>{formatAmountForDisplayWithPreferences(summary.totalRefundedAmount, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Net Sales</Typography>
              <Typography variant="h5" sx={{ color: summary.netSalesAmount > 0 ? 'success.main' : 'error.main' }}>{formatAmountForDisplayWithPreferences(summary.netSalesAmount, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Dispensing</TableCell>
              {/* <TableCell>Turnaround Days</TableCell>               */}
              <TableCell>Customer</TableCell>
              <TableCell align="right">Sub Total</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Insurance</TableCell>
              <TableCell align="right">Insurance Reversed</TableCell>
              <TableCell align="right">Refunded</TableCell>
              {/* <TableCell align="right">Returned</TableCell> */}
              <TableCell align="right">Net Earned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.invoices || []).map((invoice) => (
              <TableRow key={`${invoice.invoiceNumber}-${invoice.invoiceDate}`} selected={selectedInvoices.some((inv) => inv.id === invoice.id)} sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(invoice)}>
                <TableCell>
                  <Link
                    component="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleInvoiceClick(invoice);
                    }}
                  >
                    {invoice.invoiceNumber}
                  </Link>
                </TableCell>
                <TableCell>{formatDateWithPreferences(invoice.invoiceDate, preferences)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{Array.isArray(invoice.dispensings) ? invoice.dispensings.length : 0}</Typography>
                    {Array.isArray(invoice.dispensings) && invoice.dispensings.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(event) => openDispensingDialog(invoice, event)}
                      >
                        View
                      </Button>
                    )}
                  </Stack>
                </TableCell>
                {/* <TableCell>{invoice.turnaroundDays}</TableCell>                 */}
                <TableCell>{invoice.customerName || 'N/A'}</TableCell>
                <TableCell align="right" sx={{ color: invoice.subTotal < 0 ? 'error.main' : 'text.primary' }}>{formatAmountForDisplayWithPreferences(invoice.subTotal, preferences)}</TableCell>
                <TableCell align="right">{formatAmountForDisplayWithPreferences(invoice.discountAmount, preferences)}</TableCell>
                <TableCell align="right">{formatAmountForDisplayWithPreferences(invoice.taxAmount, preferences)}</TableCell>
                <TableCell align="right" sx={{ color: invoice.totalCustomerPayments < 0 ? 'error.main' : 'text.primary'}}>{formatAmountForDisplayWithPreferences(invoice.totalCustomerPayments, preferences)}</TableCell>
                <TableCell align="right">{formatAmountForDisplayWithPreferences(invoice.insuranceClaims, preferences)}</TableCell>
                <TableCell align="right" sx={{ color: invoice.insuranceClaimsReversed < 0 ? 'error.main' : 'text.primary' }}>{formatAmountForDisplayWithPreferences(invoice.insuranceClaimsReversed, preferences)}</TableCell>
                <TableCell align="right" sx={{ color: invoice.totalRefunded < 0 ? 'error.main' : 'text.primary' }}>{formatAmountForDisplayWithPreferences(invoice.totalRefunded, preferences)}</TableCell>
                {/* <TableCell align="right" sx={{ color: invoice.totalReturned < 0 ? 'error.main' : 'text.primary' }}>{formatAmountForDisplayWithPreferences(invoice.totalReturned, preferences)}</TableCell> */}
                <TableCell align="right" sx={{ color: invoice.netEarned > 0 ? 'success.main' : invoice.netEarned < 0 ? 'error.main' : 'text.primary' }}>{formatAmountForDisplayWithPreferences(invoice.netEarned, preferences)}</TableCell>
              </TableRow>
            ))}
            {(!data.invoices || data.invoices.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No invoices found for the selected filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {data.pagination.startIndex} to {data.pagination.endIndex} of {data.pagination.totalCount} invoices
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Pagination
              count={data.pagination.totalPages}
              page={data.pagination.currentPage}
              onChange={(event, page) => onPageChange && onPageChange(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}

      <Dialog
        open={Boolean(openDispensingInvoice)}
        onClose={closeDispensingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Dispensing Records {openDispensingInvoice?.invoiceNumber ? `- ${openDispensingInvoice.invoiceNumber}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {(!openDispensingInvoice?.dispensings || openDispensingInvoice.dispensings.length === 0) && (
            <Typography color="textSecondary">No dispensing records found.</Typography>
          )}
          {openDispensingInvoice?.dispensings && openDispensingInvoice.dispensings.length > 0 && (
            <List disablePadding>
              {openDispensingInvoice.dispensings.map((record, index) => (
                <ListItem key={`${record.id || 'dispensing'}-${index}`} divider>
                  <ListItemText
                    primary={`${index + 1}. ${record.dispensedBy || 'Unknown user'}`}
                    secondary={`Date: ${formatDateWithPreferences(record.dispensedDate, preferences)}${record.notes ? ` | Notes: ${record.notes}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDispensingDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DispenseReport;
